import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Info, Pencil, Plus, TimerOff, Trash2, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiSend } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { logger } from "@/lib/logger";
import { announcementKeys } from "@/lib/query";
import { cn } from "@/lib/utils";
import {
  type Announcement,
  type AnnouncementSeverity,
  announcementArraySchema,
  announcementSchema,
} from "@/schemas/announcements";

type StatusFilter = "all" | "active" | "scheduled" | "expired";

type FormState = {
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  dismissible: boolean;
  startsAt: string;
  expiresAt: string;
};

const emptyForm: FormState = {
  title: "",
  body: "",
  severity: "info",
  dismissible: true,
  startsAt: "",
  expiresAt: "",
};

// HTML datetime-local inputs are timezone-naive — they speak the user's
// local clock. We round-trip through ISO strings on the wire, so convert
// between local "yyyy-MM-ddTHH:mm" and an absolute Date here.
function toLocalDatetimeInputValue(input: Date | string | null): string {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDatetimeInputValue(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function announcementStatus(a: Announcement, now: Date): "active" | "scheduled" | "expired" {
  if (a.expiresAt && new Date(a.expiresAt) <= now) return "expired";
  if (a.startsAt && new Date(a.startsAt) > now) return "scheduled";
  return "active";
}

const severityMeta: Record<
  AnnouncementSeverity,
  { label: string; chip: string; icon: typeof Info }
> = {
  info: {
    label: "Info",
    chip: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100",
    icon: Info,
  },
  warning: {
    label: "Warning",
    chip: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
    icon: TriangleAlert,
  },
  critical: {
    label: "Critical",
    chip: "bg-destructive/15 text-destructive dark:bg-destructive/30 dark:text-destructive-foreground",
    icon: CircleAlert,
  },
};

const statusChip: Record<"active" | "scheduled" | "expired", string> = {
  active: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100",
  scheduled: "bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100",
  expired: "bg-muted text-muted-foreground",
};

// Datetime-local input with an explicit empty state. An empty
// <input type="datetime-local"> shows MM/DD/YYYY, HH:MM placeholder text
// that looks like a real value; this avoids the ambiguity by only
// rendering the input once the user opts in.
function DateTimeField({
  id,
  label,
  value,
  onChange,
  emptyLabel,
  setLabel,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  emptyLabel: string;
  setLabel: string;
}) {
  if (!value) {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>{label}</Label>
        <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-dashed border-input bg-muted/30 px-3">
          <span className="text-muted-foreground text-sm italic">{emptyLabel}</span>
          <button
            type="button"
            id={id}
            onClick={() => {
              // Seed with "now" rounded to the next half hour for a sensible
              // starting point. User can change immediately via the input.
              const d = new Date();
              d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
              const pad = (n: number) => n.toString().padStart(2, "0");
              onChange(
                `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
              );
            }}
            className="font-medium text-primary text-xs hover:underline"
          >
            {setLabel}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-muted-foreground text-xs hover:text-foreground"
        >
          Clear
        </button>
      </div>
      <Input
        id={id}
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function ConfigAnnouncementsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Announcement | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  // Title is the only required field. Tracked locally rather than via a
  // toast so the message renders inline under the input — the same pattern
  // Cancel Reasons uses with react-hook-form. The full RHF migration here
  // would touch every control in the dialog (6 fields + datetime widgets);
  // the inline error is the small fix that closes the QA-sweep finding
  // without a larger rewrite.
  const [titleError, setTitleError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Announcement | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("all");

  const {
    data: announcements,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: announcementKeys.admin,
    queryFn: () => apiGet("/api/config/announcements", announcementArraySchema),
  });

  // Resolve "now" once per data fetch so the filter / counts / row badges
  // can't disagree about a record's status during a single render pass. A
  // single `new Date()` shared by both memos is enough — re-renders that
  // don't change the announcement list will reuse the same reference.
  const { filtered, counts, now } = useMemo(() => {
    const computedNow = new Date();
    const initialCounts = { all: 0, active: 0, scheduled: 0, expired: 0 };
    if (!announcements) {
      return { filtered: [] as Announcement[], counts: initialCounts, now: computedNow };
    }
    const computedCounts = announcements.reduce(
      (acc, a) => {
        acc.all += 1;
        acc[announcementStatus(a, computedNow)] += 1;
        return acc;
      },
      { ...initialCounts },
    );
    const computedFiltered =
      filter === "all"
        ? announcements
        : announcements.filter((a) => announcementStatus(a, computedNow) === filter);
    return { filtered: computedFiltered, counts: computedCounts, now: computedNow };
  }, [announcements, filter]);

  const createMutation = useMutation({
    mutationFn: (data: FormState) =>
      apiSend(
        "POST",
        "/api/config/announcements",
        {
          title: data.title.trim(),
          body: data.body,
          severity: data.severity,
          dismissible: data.dismissible,
          startsAt: fromLocalDatetimeInputValue(data.startsAt),
          expiresAt: fromLocalDatetimeInputValue(data.expiresAt),
        },
        announcementSchema,
      ),
    onSuccess: () => {
      toast.success("Announcement created");
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
      closeForm();
    },
    onError: (e) => {
      logger.error("announcement_create_failed", { error: e });
      toast.error(e instanceof Error ? e.message : "Failed to create announcement");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormState }) =>
      apiSend(
        "PUT",
        `/api/config/announcements/${id}`,
        {
          title: data.title.trim(),
          body: data.body,
          severity: data.severity,
          dismissible: data.dismissible,
          startsAt: fromLocalDatetimeInputValue(data.startsAt),
          expiresAt: fromLocalDatetimeInputValue(data.expiresAt),
        },
        announcementSchema,
      ),
    onSuccess: () => {
      toast.success("Announcement updated");
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
      closeForm();
    },
    onError: (e) => {
      logger.error("announcement_update_failed", { error: e });
      toast.error(e instanceof Error ? e.message : "Failed to update announcement");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend("DELETE", `/api/config/announcements/${id}`, undefined, z.unknown()),
    onSuccess: () => {
      toast.success("Announcement deleted");
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
    onError: (e) => {
      logger.error("announcement_delete_failed", { error: e });
      toast.error(e instanceof Error ? e.message : "Failed to delete announcement");
    },
  });

  const expireMutation = useMutation({
    // "Expire now" sets expiresAt to the current instant. Cheaper UX than
    // making admins re-open the form for the common "kill this banner"
    // workflow.
    mutationFn: (id: string) =>
      apiSend(
        "PUT",
        `/api/config/announcements/${id}`,
        { expiresAt: new Date().toISOString() },
        announcementSchema,
      ),
    onSuccess: () => {
      toast.success("Announcement expired");
      queryClient.invalidateQueries({ queryKey: announcementKeys.all });
    },
    onError: (e) => {
      logger.error("announcement_expire_failed", { error: e });
      toast.error(e instanceof Error ? e.message : "Failed to expire announcement");
    },
  });

  function openCreate() {
    // Both date fields start empty. With the DateTimeField helper, that
    // renders as "Show immediately" / "No auto-expiry" with a "Pick a …
    // time" link — so the default behavior is explicit, not hidden behind
    // an empty input that looks like it has a value.
    setEditing("new");
    setForm(emptyForm);
    setTitleError(null);
  }

  function openEdit(a: Announcement) {
    setEditing(a);
    setForm({
      title: a.title,
      body: a.body,
      severity: a.severity,
      dismissible: a.dismissible,
      startsAt: toLocalDatetimeInputValue(a.startsAt),
      expiresAt: toLocalDatetimeInputValue(a.expiresAt),
    });
    setTitleError(null);
  }

  function closeForm() {
    setEditing(null);
    setForm(emptyForm);
    setTitleError(null);
  }

  function handleSubmit() {
    if (form.title.trim().length === 0) {
      setTitleError("Title is required");
      return;
    }
    setTitleError(null);
    if (editing === "new") {
      createMutation.mutate(form);
    } else if (editing) {
      updateMutation.mutate({ id: editing.id, data: form });
    }
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <div className="flex items-center justify-between rounded-md border bg-card p-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Announcements</h1>
          <p className="mt-1 text-muted-foreground">
            Banner messages shown to all signed-in users until they expire or are dismissed.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="size-4" />
          New Announcement
        </Button>
      </div>

      <Card className="mt-6">
        <CardHeader className="flex flex-row flex-wrap items-center gap-2 px-6 pt-6">
          {(["all", "active", "scheduled", "expired"] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm capitalize transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                filter === key
                  ? "border-primary bg-primary text-primary-foreground"
                  : "bg-card hover:bg-accent",
              )}
            >
              {key}
              {counts[key] > 0 && <span className="ml-1 text-xs opacity-70">({counts[key]})</span>}
            </button>
          ))}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {["sk-0", "sk-1", "sk-2"].map((k) => (
                <div key={k} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : isError ? (
            <ErrorState message="Couldn't load announcements." onRetry={() => refetch()} />
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              {announcements && announcements.length === 0
                ? 'No announcements yet. Click "New Announcement" to create one.'
                : "No announcements match this filter."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 pr-4 font-medium">Title</th>
                    <th className="pb-3 pr-4 font-medium">Severity</th>
                    <th className="pb-3 pr-4 font-medium">Status</th>
                    <th className="pb-3 pr-4 font-medium">Starts</th>
                    <th className="pb-3 pr-4 font-medium">Expires</th>
                    <th className="pb-3 pr-4 font-medium">Dismissible</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    const status = announcementStatus(a, now);
                    const sevMeta = severityMeta[a.severity];
                    const SevIcon = sevMeta.icon;
                    return (
                      <tr key={a.id} className="border-b align-top">
                        <td className="py-3 pr-4">
                          <div className="font-medium">{a.title}</div>
                          {a.body && (
                            <div className="mt-0.5 line-clamp-2 text-muted-foreground text-xs">
                              {a.body}
                            </div>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
                              sevMeta.chip,
                            )}
                          >
                            <SevIcon className="size-3" />
                            {sevMeta.label}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-block rounded-full px-2 py-0.5 text-xs capitalize",
                              statusChip[status],
                            )}
                          >
                            {status}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          {a.startsAt ? formatDate(a.startsAt) : "Immediately"}
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          {a.expiresAt ? formatDate(a.expiresAt) : "No expiry"}
                        </td>
                        <td className="py-3 pr-4 text-muted-foreground">
                          {a.dismissible ? "Yes" : "No"}
                        </td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label="Edit"
                              onClick={() => openEdit(a)}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            {status === "active" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => expireMutation.mutate(a.id)}
                                disabled={expireMutation.isPending}
                                title="Expire now — set the expiry to right now so this banner stops showing"
                              >
                                <TimerOff className="size-4" />
                                Expire
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label="Delete"
                              onClick={() => setPendingDelete(a)}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) closeForm();
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "New Announcement" : "Edit Announcement"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label
                htmlFor="announcement-title"
                className={titleError ? "text-destructive" : undefined}
              >
                Title
              </Label>
              <Input
                id="announcement-title"
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value });
                  if (titleError && e.target.value.trim().length > 0) setTitleError(null);
                }}
                placeholder="Brief heading shown in bold"
                aria-invalid={titleError ? true : undefined}
                aria-describedby={titleError ? "announcement-title-error" : undefined}
              />
              {titleError && (
                <p id="announcement-title-error" className="text-destructive text-sm">
                  {titleError}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="announcement-body">Message</Label>
              <Textarea
                id="announcement-body"
                rows={3}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Optional message body shown below the title"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="announcement-severity">Severity</Label>
                <Select
                  value={form.severity}
                  onValueChange={(v: AnnouncementSeverity) => setForm({ ...form, severity: v })}
                >
                  <SelectTrigger id="announcement-severity" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                {/* Invisible label baseline-aligns the checkbox row with the Severity select beside it. */}
                <Label aria-hidden="true" className="invisible">
                  &nbsp;
                </Label>
                <div className="flex h-9 items-center gap-2">
                  <Checkbox
                    id="announcement-dismissible"
                    checked={form.dismissible}
                    onCheckedChange={(c) => setForm({ ...form, dismissible: c === true })}
                  />
                  <Label htmlFor="announcement-dismissible" className="cursor-pointer font-normal">
                    Users can dismiss this banner
                  </Label>
                </div>
              </div>
            </div>
            {/* Browser quirk: an empty <input type="datetime-local"> still
                renders MM/DD/YYYY, HH:MM placeholder text, which looks
                indistinguishable from a real value. We dodge it entirely:
                when the field is empty, hide the input and show an
                explicit "Set X" link. The user opts in only if they want
                a specific time; otherwise the dialog clearly shows the
                default behavior (show immediately / no auto-expiry). */}
            <div className="grid gap-4 sm:grid-cols-2">
              <DateTimeField
                id="announcement-starts"
                label="Starts at"
                value={form.startsAt}
                onChange={(next) => setForm({ ...form, startsAt: next })}
                emptyLabel="Show immediately"
                setLabel="Pick a start time"
              />
              <DateTimeField
                id="announcement-expires"
                label="Expires at"
                value={form.expiresAt}
                onChange={(next) => setForm({ ...form, expiresAt: next })}
                emptyLabel="No auto-expiry"
                setLabel="Pick an expiry time"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isMutating}>
              {isMutating ? "Saving..." : editing === "new" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Delete announcement?"
        description={
          pendingDelete
            ? `This will permanently remove "${pendingDelete.title}" and all of its dismissal records. Use "Expire now" instead if you just want to hide it.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
